import React, { useMemo } from 'react'
import { usePeoAlignmentStats, type PeoAlignmentRow } from '../../hooks/useAnalytics'
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts'
import { Target, Users, Award, GraduationCap, Loader2, AlertTriangle } from 'lucide-react'

interface Props {
  department: string | null
  program: string | null
  batchYear: number | null
}

// ── Helpers ────────────────────────────────────────────────────
function scoreBg(score: number | null): string {
  if (score === null) return 'bg-gray-100 text-gray-400 border-gray-200'
  if (score >= 70) return 'bg-green-50 text-green-700 border-green-200'
  if (score >= 50) return 'bg-yellow-50 text-yellow-700 border-yellow-200'
  return 'bg-red-50 text-red-700 border-red-200'
}

const PROGRAM_COLORS = [
  '#1e3a5f', '#6366f1', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#7c3aed', '#db2777', '#2563eb', '#84cc16',
]

// Build per-type section data (radar, bar, heatmap) for one outcome type ('PEO' | 'PO')
function buildSectionData(data: PeoAlignmentRow[], type: 'PEO' | 'PO') {
  const rows = data.filter(r => r.outcome_type === type)
  if (rows.length === 0) return null

  // Programs that actually have this outcome type
  const programs = [...new Set(rows.map(r => r.program_code))].sort()

  // Unique outcome keys sorted by number
  const outcomeKeys = [...new Set(rows.map(r => `${type} ${r.peo_number}`))]
    .sort((a, b) => {
      const na = parseInt(a.split(' ')[1])
      const nb = parseInt(b.split(' ')[1])
      return na - nb
    })

  // Heatmap: null = program doesn't have this outcome (N/A)
  const heatmapRows = programs.map(pc => {
    const scores: Record<string, number | null> = {}
    const attainment: Record<string, number | null> = {}
    outcomeKeys.forEach(key => {
      const num = parseInt(key.split(' ')[1])
      const row = rows.find(r => r.program_code === pc && r.peo_number === num)
      scores[key] = row ? row.avg_score : null
      attainment[key] = row ? row.attainment_rate : null
    })
    return { program: pc, scores, attainment }
  })

  // Radar: only include programs that have this type; use actual score (no fake 0)
  const radarData = outcomeKeys.map(key => {
    const num = parseInt(key.split(' ')[1])
    const point: Record<string, any> = { outcome: key }
    programs.forEach(pc => {
      const row = rows.find(r => r.program_code === pc && r.peo_number === num)
      // undefined → Recharts skips the point gracefully
      point[pc] = row ? row.avg_score : undefined
    })
    return point
  })

  // Bar: same — undefined for missing
  const barData = outcomeKeys.map(key => {
    const num = parseInt(key.split(' ')[1])
    const entry: Record<string, any> = { outcome: key }
    programs.forEach(pc => {
      const row = rows.find(r => r.program_code === pc && r.peo_number === num)
      entry[pc] = row ? row.avg_score : undefined
    })
    return entry
  })

  return { programs, outcomeKeys, heatmapRows, radarData, barData }
}

// ── Sub-component: one outcome-type section ─────────────────────
interface SectionProps {
  type: 'PEO' | 'PO'
  section: NonNullable<ReturnType<typeof buildSectionData>>
}

const OutcomeSection: React.FC<SectionProps> = ({ type, section }) => {
  const { programs, outcomeKeys, heatmapRows, radarData, barData } = section
  const label = type === 'PEO' ? 'Program Educational Outcomes (PEO)' : 'Program Outcomes (PO)'
  const badge = type === 'PEO'
    ? 'bg-blue-100 text-blue-700 border-blue-200'
    : 'bg-purple-100 text-purple-700 border-purple-200'

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${badge}`}>{type}</span>
        <h2 className="text-sm font-bold text-gray-700">{label}</h2>
        <span className="text-xs text-gray-400 ml-auto">{programs.length} program{programs.length !== 1 ? 's' : ''} · {outcomeKeys.length} outcome{outcomeKeys.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Radar chart */}
      {programs.length <= 6 && radarData.length >= 3 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-bold text-gray-500 mb-3">Scores by Program</p>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="outcome" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              {programs.map((pc, i) => (
                <Radar
                  key={pc}
                  name={pc}
                  dataKey={pc}
                  stroke={PROGRAM_COLORS[i % PROGRAM_COLORS.length]}
                  fill={PROGRAM_COLORS[i % PROGRAM_COLORS.length]}
                  fillOpacity={0.15}
                  connectNulls={false}
                />
              ))}
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bar chart — full width */}
      {barData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-bold text-gray-500 mb-3">Average Scores by Outcome</p>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="outcome" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {programs.map((pc, i) => (
                <Bar key={pc} dataKey={pc} fill={PROGRAM_COLORS[i % PROGRAM_COLORS.length]} radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Heatmap */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-gray-500">Score Heatmap</p>
          <p className="text-xs text-gray-400">Each cell: <span className="font-semibold">avg score</span> · <span className="font-semibold">attainment%</span></p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-50">Program</th>
                {outcomeKeys.map(o => (
                  <th key={o} className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-50 whitespace-nowrap">{o}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmapRows.map((row, idx) => (
                <tr key={row.program} className={idx % 2 === 0 ? '' : 'bg-gray-50/50'}>
                  <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{row.program}</td>
                  {outcomeKeys.map(o => {
                    const score = row.scores[o]
                    const att = row.attainment[o]
                    // Color based on worst of score vs attainment
                    const worstVal = Math.min(score ?? 100, att ?? 100)
                    const cellStyle = scoreBg(worstVal === 100 ? score : worstVal)
                    // Flag when score is good but attainment is weak
                    const attWeak = score !== null && att !== null && score >= 70 && att < 70
                    return (
                      <td key={o} className="text-center px-2 py-2">
                        {score === null ? (
                          <span className="inline-block px-2.5 py-1 rounded-md text-xs font-bold border bg-gray-100 text-gray-400 border-gray-200">N/A</span>
                        ) : (
                          <span className={`inline-flex flex-col items-center px-2 py-1 rounded-md text-xs font-bold border ${cellStyle}`}>
                            <span>{score.toFixed(0)}</span>
                            {att !== null && (
                              <span className={`text-[10px] font-semibold leading-none mt-0.5 ${attWeak ? 'text-orange-600' : ''}`}>
                                {att.toFixed(0)}%
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /> Score ≥70 &amp; Attainment ≥70%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500" /> Score or Attainment 50–69%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500" /> Score or Attainment &lt;50%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200" /> N/A</span>
        </div>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────
const PeoAlignmentTab: React.FC<Props> = ({ department, program, batchYear }) => {
  const { data, loading, error } = usePeoAlignmentStats(department, program, batchYear)

  const stats = useMemo(() => {
    if (!data || data.length === 0) return null
    const allScores = data.map(r => r.avg_score)
    const allAttainment = data.map(r => r.attainment_rate).filter(r => r != null)
    return {
      overallAttainment: allAttainment.length > 0
        ? Math.round(allAttainment.reduce((a, b) => a + b, 0) / allAttainment.length)
        : 0,
      avgScore: allScores.length > 0
        ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length)
        : 0,
      programCount: new Set(data.map(r => r.program_code)).size,
      totalRespondents: (() => {
        const perProgram = new Map<string, number>()
        data.forEach(r => {
          perProgram.set(r.program_code, Math.max(perProgram.get(r.program_code) ?? 0, Number(r.respondent_count)))
        })
        return Array.from(perProgram.values()).reduce((a, b) => a + b, 0)
      })(),
    }
  }, [data])

  const peoSection = useMemo(() => data ? buildSectionData(data, 'PEO') : null, [data])
  const poSection  = useMemo(() => data ? buildSectionData(data, 'PO')  : null, [data])

  // ── Loading / Error / Empty ──────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
        <span className="ml-2 text-sm text-gray-500">Loading PEO/PO data...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-800">Failed to load PEO alignment data</p>
          <p className="text-xs text-red-600 mt-0.5">{error}</p>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    const scope = program
      ? `program ${program}`
      : department
        ? `the ${department}`
        : 'any department'
    const batchSuffix = batchYear ? ` (Class of ${batchYear})` : ''
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-base font-bold text-gray-700 mb-1">No PEO/PO Scores Found</h3>
        <p className="text-sm text-gray-500">
          No outcome assessment data exists for <span className="font-medium">{scope}</span>{batchSuffix}.
        </p>
        <p className="text-xs text-gray-400 mt-1.5">Alumni in this selection may not have completed a PEO/PO survey yet.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Award className="w-4 h-4 text-green-600" />
              Attainment Rate
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.overallAttainment}%</p>
            <p className="text-xs text-gray-400 mt-0.5">scoring ≥70%</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Target className="w-4 h-4 text-blue-600" />
              Average Score
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avgScore}</p>
            <p className="text-xs text-gray-400 mt-0.5">across all outcomes</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <GraduationCap className="w-4 h-4 text-indigo-600" />
              Programs
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.programCount}</p>
            <p className="text-xs text-gray-400 mt-0.5">with assessment data</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Users className="w-4 h-4 text-emerald-600" />
              Total Respondents
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totalRespondents}</p>
            <p className="text-xs text-gray-400 mt-0.5">unique alumni with PEO/PO scores</p>
          </div>
        </div>
      )}

      {/* PEO Section */}
      {peoSection && <OutcomeSection type="PEO" section={peoSection} />}

      {/* PO Section */}
      {poSection && <OutcomeSection type="PO" section={poSection} />}
    </div>
  )
}

export default PeoAlignmentTab
